// File: src/components/questionnaires/JourdaResultsScreen.js
// -----------------------------------------------------------------------------
// Écran des résultats spécifique au questionnaire Jourda
// -----------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { generateJourdaSynthesis, sendJourdaAgreement } from '../../api/questionnaires';
import ParentShareHybrid from './ParentShareHybrid';
import { jourdaResultsConfig } from '../../data/resultsScreenConfigs';

// Configuration des 4 dimensions
const dimensionsConfig = [
  { key: 'F', name: 'Expression', subtitle: 'Créativité, émotions, liberté', emoji: '🎨', color: '#a855f7' },
  { key: 'R', name: 'Principes', subtitle: 'Valeurs, engagement, loyauté', emoji: '⚖️', color: '#22c55e' },
  { key: 'P', name: 'Réflexion', subtitle: 'Analyse, compréhension, logique', emoji: '🧠', color: '#3b82f6' },
  { key: 'M', name: 'Action', subtitle: 'Résultats, concret, efficacité', emoji: '🎯', color: '#f59e0b' }
];

// Configuration des profils
const profilesConfig = {
  animateur: {
    name: "Animateur",
    subtitle: "Expressif, entraînant, communicatif",
    emoji: "✨",
    color: "#F59E0B", // ambre
    description: "Tu as besoin de t'exprimer, de partager, d'entraîner les autres. Tu as du charisme naturel et tu sais créer une ambiance. Quand quelque chose te passionne, tu rayonnes et tu donnes envie aux autres de te suivre."
  },
  regulateur: {
    name: "Régulateur",
    subtitle: "Structurant, fiable, appliqué",
    emoji: "⚙️",
    color: "#64748B", // slate
    description: "Tu aimes que les choses soient bien faites, dans les règles. Tu as le sens de l'ordre et tu sais faire respecter un cadre. On peut compter sur toi pour que tout fonctionne correctement."
  },
  analyste: {
    name: "Analyste",
    subtitle: "Réfléchi, logique, profond",
    emoji: "🔍",
    color: "#2563EB", // blue
    description: "Tu as besoin de comprendre comment les choses fonctionnent. Tu aimes réfléchir, analyser, aller au fond des sujets. Les réponses superficielles ne te suffisent pas : tu veux vraiment saisir la logique derrière."
  },
  realisateur: {
    name: "Réalisateur",
    subtitle: "Concret, efficace, pragmatique",
    emoji: "🎯",
    color: "#16A34A", // green
    description: "Tu aimes voir des résultats concrets. Les grandes théories, c'est bien, mais toi tu préfères agir et construire quelque chose de réel. Tu es pragmatique : ce qui compte, c'est que ça marche."
  },
  entrepreneur: {
    name: "Entrepreneur",
    subtitle: "Créatif et pragmatique",
    emoji: "🚀",
    color: "#DC2626", // red
    description: "Tu as des idées et tu veux les concrétiser. Tu ne te contentes pas de rêver : tu passes à l'action pour que tes projets deviennent réalité. Tu as le goût du risque et l'envie de créer quelque chose qui t'appartient."
  },
  innovateur: {
    name: "Innovateur",
    subtitle: "Imaginatif, audacieux, original",
    emoji: "💡",
    color: "#A855F7", // violet
    description: "Tu as une façon de penser qui sort des sentiers battus. Tu adores les idées nouvelles, les approches originales. Là où d'autres voient des certitudes, toi tu vois des questions à explorer et des territoires inconnus."
  },
  organisateur: {
    name: "Organisateur",
    subtitle: "Rigoureux, méthodique, critique",
    emoji: "📋",
    color: "#0F766E", // teal
    description: "Tu as l'esprit d'analyse et le sens de la méthode. Tu aimes que les choses soient bien structurées, logiques, vérifiables. Tu repères vite ce qui ne tient pas debout et tu sais organiser un travail de façon efficace."
  },
  engage: {
    name: "Engagé",
    subtitle: "Loyal, engagé, au service des autres",
    emoji: "🛡️",
    color: "#1D4ED8", // indigo
    description: "Tu as des valeurs fortes et tu veux qu'elles se traduisent en actes. Tu es loyal, fiable, et tu te mets naturellement au service d'une cause ou d'un collectif. L'intérêt général compte plus pour toi que ton intérêt personnel."
  },
  artisan: {
    name: "Artisan",
    subtitle: "Autonome, créateur, maître de son œuvre",
    emoji: "🛠️",
    color: "#92400E", // amber dark
    description: "Tu aimes créer quelque chose qui te ressemble, de tes propres mains ou à ta façon. Tu as besoin d'autonomie : travailler pour toi-même ou en indépendant pourrait bien te convenir. Tu veux maîtriser ton œuvre de A à Z."
  },
  metteur_en_scene: {
    name: "Metteur en Scène",
    subtitle: "Leader créatif, visionnaire, fédérateur",
    emoji: "🎬",
    color: "#BE185D", // pink
    description: "Tu as des idées et tu sais les défendre. Tu aimes diriger un projet, coordonner une équipe, donner une vision. Tu ne fais pas forcément tout toi-même, mais tu inspires, tu guides, tu fais en sorte que ça se réalise."
  },
  batisseur: {
    name: "Bâtisseur",
    subtitle: "Constructeur, persévérant, ambitieux",
    emoji: "🏗️",
    color: "#78350F", // brown
    description: "Tu veux construire quelque chose qui dure, qui a du sens. Tu es prêt à y mettre le temps et l'énergie. Ce n'est pas juste un travail pour toi : c'est une œuvre, quelque chose dont tu pourras être fier sur le long terme."
  },
  pilote: {
    name: "Pilote",
    subtitle: "Fiable, utile, indispensable",
    emoji: "🧭",
    color: "#065F46", // emerald dark
    description: "Tu aimes que les choses fonctionnent bien. Quand quelque chose coince, tu interviens pour remettre de l'ordre. On compte sur toi pour que tout tourne rond. Tu tires ta satisfaction d'être utile et efficace au quotidien."
  },
  stratege: {
    name: "Stratège",
    subtitle: "Visionnaire, anticipateur, stratégique",
    emoji: "♟️",
    color: "#111827", // gray dark
    description: "Tu vois loin. Là où d'autres se concentrent sur le présent, toi tu penses déjà à demain, à l'année prochaine, aux grandes tendances. Tu aimes élaborer des plans, anticiper les évolutions, imaginer des scénarios."
  },
  expert: {
    name: "Expert",
    subtitle: "Précis, exigeant, évaluateur",
    emoji: "🎓",
    color: "#374151", // gray
    description: "Tu as l'œil pour repérer ce qui va et ce qui ne va pas. Tu sais appliquer des critères, comparer à des références, évaluer une qualité. Dans un travail ou un projet, tu es celui qui vérifie que tout est au niveau attendu."
  },
  gardien: {
    name: "Gardien",
    subtitle: "Vigilant, protecteur, garant de l'ordre",
    emoji: "👁️",
    color: "#7C2D12", // red brown
    description: "Tu as le sens des responsabilités. Tu veilles à ce que les règles soient respectées, que tout se passe bien. On peut te confier la garde de quelque chose d'important : tu seras vigilant et tu prendras ton rôle au sérieux."
  },
  verificateur: {
    name: "Vérificateur",
    subtitle: "Critique, améliorateur, rigoureux",
    emoji: "✓",
    color: "#1F2937", // gray dark
    description: "Tu ne prends rien pour acquis. Tu vérifies, tu questionnes, tu cherches ce qui pourrait être amélioré. Ce n'est pas pour critiquer : c'est pour que les choses soient vraiment bien faites, sans erreur ni approximation."
  },
  explorateur: {
    name: "Explorateur",
    subtitle: "Adaptable, curieux, ouvert",
    emoji: "🌟",
    color: "#0EA5E9", // sky
    description: "Tu n'es pas enfermé dans une seule case, et c'est une force. Tu peux t'adapter à plein de situations différentes. À ton âge, c'est normal de ne pas encore avoir de direction très marquée : tu es en phase d'exploration, et c'est très bien comme ça."
  }
};


// Modale pour afficher les détails
function DetailModal({ item, onClose, isAdjacent = false }) {
  if (!item) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-800 rounded-2xl p-6 max-w-md w-full relative"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-400 hover:text-white"
        >
          ✕
        </button>
        <div className="text-4xl text-center mb-3">{item.emoji}</div>
        <h3 className="text-xl font-bold text-white text-center mb-2">{item.name}</h3>
        <p className="text-sm text-zinc-400 text-center mb-4">{item.subtitle}</p>
        <div className="h-px bg-zinc-700 mb-4"></div>
        {isAdjacent && (
          <p className="text-sm text-indigo-400 mb-3 italic">
            Ce profil est proche du tien — tu partages certains de ces traits :
          </p>
        )}
        <p className="text-zinc-300 text-sm leading-relaxed">{item.description}</p>
      </motion.div>
    </motion.div>
  );
}

// Modale pour les dimensions
function DimensionModal({ dimension, onClose }) {
  if (!dimension) return null;

  const descriptions = {
    F: "Cette dimension reflète ton besoin de t'exprimer, de créer, de vivre des émotions fortes. Tu aimes les environnements qui laissent place à l'originalité et à la liberté.",
    R: "Cette dimension reflète ton attachement aux valeurs, à ce qui est juste et correct. Tu as besoin de sens dans ce que tu fais et tu es quelqu'un de loyal.",
    P: "Cette dimension reflète ton besoin de comprendre, d'analyser, de maîtriser les sujets en profondeur. Tu apprécies la logique et la réflexion.",
    M: "Cette dimension reflète ton besoin d'agir, de voir des résultats concrets. Tu es pragmatique et tu aimes que les choses avancent."
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-800 rounded-2xl p-6 max-w-md w-full relative"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-400 hover:text-white"
        >
          ✕
        </button>
        <div className="text-4xl text-center mb-3">{dimension.emoji}</div>
        <h3 className="text-xl font-bold text-white text-center mb-2">{dimension.name}</h3>
        <p className="text-sm text-zinc-400 text-center mb-4">{dimension.subtitle}</p>
        <div className="h-px bg-zinc-700 mb-4"></div>
        <p className="text-zinc-300 text-sm leading-relaxed">{descriptions[dimension.key]}</p>
      </motion.div>
    </motion.div>
  );
}

export default function JourdaResultsScreen({ results: initialResults, onRestart }) {
  const [synthesis, setSynthesis] = useState(initialResults?.synthesis || '');
  const [isLoadingSynthesis, setIsLoadingSynthesis] = useState(false);
  const [synthesisError, setSynthesisError] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedAdjacent, setSelectedAdjacent] = useState(null);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const {
    attribution,
    disclaimer
  } = jourdaResultsConfig;

  const profile = initialResults?.results?.profile;
  const scores = profile?.scores || {};

  // Profil principal et adjacents
  const mainProfile = profile?.profileType ? profilesConfig[profile.profileType] : null;
  const adjacentProfilesData = (profile?.adjacentProfiles || []).map(key => ({
    key,
    ...profilesConfig[key]
  })).filter(p => p.name);

  // Dimensions triées par score
  const sortedDimensions = [...dimensionsConfig].sort((a, b) => (scores[b.key] || 0) - (scores[a.key] || 0));

  // Générer la synthèse si elle n'existe pas
  useEffect(() => {
    if (!synthesis && initialResults?.id) {
      handleGenerateSynthesis();
    }
  }, [initialResults?.id]);

  const handleGenerateSynthesis = async () => {
    setIsLoadingSynthesis(true);
    setSynthesisError(false);
    try {
      const response = await generateJourdaSynthesis();
      setSynthesis(response.synthesis || '');
    } catch (error) {
      console.error('Erreur génération synthèse:', error);
      setSynthesisError(true);
    } finally {
      setIsLoadingSynthesis(false);
    }
  };

  // Parser la synthèse markdown en HTML
  const parseSynthesis = (text) => {
    if (!text) return '';

    let html = text
      .replace(/^#{1,6}\s*/gm, '')
      .replace(/^\*\*([^*]+)\*\*$/gm, '\n<h4><strong>$1</strong></h4>\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    const blocks = html.split(/\n\n+/);

    return blocks.map(block => {
      block = block.trim();
      if (!block) return '';
      if (block.startsWith('<h4>')) return block;
      return `<p>${block.replace(/\n/g, ' ')}</p>`;
    }).join('\n');
  };

  if (!profile) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <p className="text-zinc-400">Aucun résultat disponible</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-900 min-h-screen relative overflow-y-auto">
      {/* Blobs decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-72 h-72 rounded-full blur-[80px] opacity-30 bg-indigo-500 -top-24 -right-24 animate-blob1" />
        <div className="absolute w-52 h-52 rounded-full blur-[80px] opacity-25 bg-pink-500 bottom-1/5 -right-16 animate-blob3" />
      </div>

      <div className="w-full md:max-w-[50%] mx-auto p-5">
        {/* Header */}
        <div className="p-6 text-center">
          <h1 className="text-2xl font-bold text-white">Comment tu fonctionnes</h1>
        </div>

        {/* Profil principal */}
        {mainProfile && (
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-[20px] p-6 mb-5">
            <h2 className="text-lg font-semibold text-white mb-4">Ton profil</h2>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4 p-4 bg-white/5 border border-white/[0.08] border-l-4 rounded-xl cursor-pointer hover:bg-white/10 transition"
              style={{ borderLeftColor: mainProfile.color }}
              onClick={() => setSelectedProfile(mainProfile)}
            >
              <div className="text-3xl">{mainProfile.emoji}</div>
              <div className="flex-1">
                <div className="text-white font-semibold">{mainProfile.name}</div>
                <div className="text-sm text-zinc-400">{mainProfile.subtitle}</div>
              </div>
              <div className="text-zinc-500">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4M12 8h.01"/>
                </svg>
              </div>
            </motion.div>
          </div>
        )}

        {/* Profils adjacents */}
        {adjacentProfilesData.length > 0 && (
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-[20px] p-6 mb-5">
            <p className="text-sm text-zinc-400 mb-3">Tu as aussi des points communs avec :</p>
            <div className="flex flex-wrap gap-2">
              {adjacentProfilesData.map((adj) => (
                <button
                  key={adj.key}
                  className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-white hover:bg-white/10 transition"
                  onClick={() => setSelectedAdjacent(adj)}
                >
                  <span>{adj.emoji}</span>
                  <span>{adj.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 4 Dimensions */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-[20px] p-6 mb-5">
          <h2 className="text-lg font-semibold text-white mb-2">Tes 4 moteurs</h2>
          <p className="text-xs text-zinc-500 mb-4">Appuie sur un moteur pour en savoir plus</p>
          <div className="space-y-3">
            {sortedDimensions.map((dim, index) => {
              const score = scores[dim.key] || 0;
              const percentage = (score / 10) * 100;

              return (
                <motion.div
                  key={dim.key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3 cursor-pointer hover:bg-white/5 p-2 -mx-2 rounded-lg transition"
                  onClick={() => setSelectedDimension(dim)}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: dim.color }}
                  >
                    #{index + 1}
                  </div>
                  <div className="flex items-center gap-2 w-24">
                    <span className="text-lg">{dim.emoji}</span>
                    <span className="text-sm text-white">{dim.name}</span>
                  </div>
                  <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: dim.color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.max(10, percentage)}%` }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Synthèse IA */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-[20px] p-6 mb-5">
          <h2 className="text-lg font-semibold text-white mb-4">Ce que ça révèle</h2>

          {isLoadingSynthesis ? (
            <div className="flex flex-col items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500 mb-3"></div>
              <p className="text-zinc-400 text-sm">Analyse en cours...</p>
            </div>
          ) : synthesisError ? (
            <div className="text-center py-6">
              <p className="text-zinc-400 mb-2">La synthèse n'a pas pu être générée.</p>
              <button
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-lg transition"
                onClick={handleGenerateSynthesis}
              >
                Réessayer la génération
              </button>
            </div>
          ) : (
            <>
              
                <div
                  className="synthesis-text"
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      if (!synthesis) return '';

                      let html = synthesis
                        // **Titre** → <h4><strong>Titre</strong></h4>
                        .replace(/\*\*([^*]+)\*\*\s*/g, '\n<h4><strong>$1</strong></h4>\n')
                        .replace(/\n{3,}/g, '\n\n')
                        .trim();

                      const blocks = html.split(/\n\n+/);

                      return blocks
                        .map(block => {
                          block = block.trim();
                          if (!block) return '';

                          // 🟢 TITRE
                          if (block.startsWith('<h4>')) {
                            return block;
                          }

                          // 🟢 LISTE (- ...)
                          if (block.split('\n').every(line => line.trim().startsWith('- '))) {
                            const items = block
                              .split('\n')
                              .map(line => `<li>${line.replace(/^-+\s*/, '')}</li>`)
                              .join('\n');

                            return `<ul>${items}</ul>`;
                          }

                          // 🟢 PARAGRAPHE
                          return `<p>${block.replace(/\n/g, ' ')}</p>`;
                        })
                        .join('\n');
                    })()
                  }}
                />

              <div className="synthesis-disclaimer">
                {disclaimer}
              </div>
            </>
          )}
        </div>

        {/* Partage parents */}
        {!isLoadingSynthesis && (
          <div className="text-center">
            <ParentShareHybrid onSendAgreement={sendJourdaAgreement} agreed={initialResults.share_to_parent} />
          </div>
        )}

        {/* Bouton recommencer */}
        {/* {!isLoadingSynthesis && onRestart && (
          <div className="mb-5 text-center">
            <button
              className="py-3 px-4 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.08] text-zinc-400 hover:text-zinc-300 text-sm rounded-xl transition"
              onClick={onRestart}
            >
              Recommencer le test
            </button>
          </div>
        )} */}

        <p className="text-center text-xs text-slate-600 leading-relaxed whitespace-pre-line">
          {attribution}
        </p>
      </div>

      {/* Modales */}
      <AnimatePresence>
        {selectedProfile && (
          <DetailModal item={selectedProfile} onClose={() => setSelectedProfile(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedAdjacent && (
          <DetailModal item={selectedAdjacent} onClose={() => setSelectedAdjacent(null)} isAdjacent />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedDimension && (
          <DimensionModal dimension={selectedDimension} onClose={() => setSelectedDimension(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}
